import { inject, Injectable } from "@angular/core";
import { RoleApi } from "../Api/RoleApi";
import { RoleState } from "./RoleState";
import { SearchRoleRequest } from "../Dto/RoleSearchRequest";
import { CreateRoleRequest } from "../Dto/RoleCreateRequest";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class RoleEffects {
  private readonly roleApi = inject(RoleApi);
  private readonly roleState = inject(RoleState);

  async searchRoles(params: SearchRoleRequest = {}): Promise<void> {
    const mergedParams: SearchRoleRequest = {
      ...this.roleState.searchParams(),
      ...params,
    };

    this.roleState.setLoading(true);
    this.roleState.setError(null);

    try {
      const response = await this.roleApi.searchRoles(mergedParams);
      this.roleState.setRoles(response.data, response.pagination);
      this.roleState.setSearchParams(mergedParams);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.roleState.setError(errorMessage);
    } finally {
      this.roleState.setLoading(false);
    }
  }

  async setRolePermissions(roleId: string, permissionIds: string[]): Promise<boolean> {
    this.roleState.setLoading(true);
    this.roleState.setError(null);

    try {
      await this.roleApi.setRolePermissions(roleId, permissionIds);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.roleState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.roleState.setLoading(false);
    }
  }

  async createRole(request: CreateRoleRequest): Promise<boolean> {
    this.roleState.setLoading(true);
    this.roleState.setError(null);

    try {
      await this.roleApi.createRole(request);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.roleState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.roleState.setLoading(false);
    }
  }

  async deleteRole(roleId: string): Promise<boolean> {
    this.roleState.setLoading(true);
    this.roleState.setError(null);

    try {
      await this.roleApi.deleteRole(roleId);
      this.roleState.removeRoleFromState(roleId);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.roleState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.roleState.setLoading(false);
    }
  }
}
